import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, Download, Eye, TrendingUp, Building2, CheckCircle, Award, Search } from "lucide-react";
import { bondsAPI, formatDate, allocationRequestsAPI } from "../../services/api";
import { createResponseSelector } from "../../utils/apiHelpers";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const STATUS_CONFIG = {
    Draft: { className: "bg-gray-100 text-gray-800", icon: "📝" },
    Approved: { className: "bg-green-100 text-green-800", icon: "✅" },
    Listed: { className: "bg-green-100 text-green-800", icon: "🏛️" },
    Active: { className: "bg-green-100 text-green-800", icon: "✅" },
    Matured: { className: "bg-blue-100 text-blue-800", icon: "🏁" },
};

const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { className: "bg-gray-100 text-gray-800", icon: "" };
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
            {config.icon && <span className="mr-1">{config.icon}</span>}
            {status}
        </span>
    );
};

const InvestorDocumentManagement = () => {
    const navigate = useNavigate();
    const [selectedSection, setSelectedSection] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    const documentSections = [
        {
            id: "daily-accrual-profit",
            title: "Daily Accrual Profit Report",
            icon: TrendingUp,
            description: "Daily profit accrual calculations and reports"
        },
        {
            id: "transaction-file",
            title: "Transaction File",
            icon: FileText,
            description: "Complete transaction history and records"
        },
        {
            id: "audited-financial",
            title: "Audited Financial Statements",
            icon: Building2,
            description: "Audited financial statements and reports"
        },
        {
            id: "unaudited-accounts",
            title: "Unaudited Management Accounts",
            icon: FileText,
            description: "Unaudited management accounts and reports"
        },
        {
            id: "compliance-certificate",
            title: "Compliance Certificate",
            icon: CheckCircle,
            description: "Compliance certificates and documentation"
        },
        {
            id: "ptc",
            title: "Pass Through Certificate (PTC)",
            icon: Award,
            description: "Pass through certificates for investments"
        }
    ];

    // Fetch allocation requests to get bonds with approved requests
    const { data: allocationRequests, isLoading: isLoadingRequests } = useQuery({
        queryKey: ['myAllocationRequests'],
        queryFn: async () => {
            const response = await allocationRequestsAPI.getMyRequests();
            return response;
        },
        select: data => {
            const responseData = data.data;
            if (responseData && responseData.success && responseData.data) {
                return responseData.data.requests || [];
            }
            return [];
        },
    });

    // Get bonds with allocation requests
    const approvedBonds = React.useMemo(() => {
        if (!allocationRequests || allocationRequests.length === 0) {
            return [];
        }

        // Extract unique bonds from all allocation requests
        const bondMap = new Map();
        allocationRequests.forEach(request => {
            const bond = request.bondId;
            const bondSnapshot = request.bondSnapshot;

            if (bond && typeof bond === 'object') {
                const id = bond._id || bond.id;
                if (!bondMap.has(id)) {
                    bondMap.set(id, {
                        _id: id,
                        bondName: bond.bondName || bondSnapshot?.bondName || 'Unknown Bond',
                        bondNumber: bond.bondNumber || bondSnapshot?.bondNumber,
                        issuer: bond.issuer || bondSnapshot?.issuer,
                        status: bond.status || bondSnapshot?.status,
                        issueDate: bond.issueDate || bondSnapshot?.subscriptionStartDate,
                        maturityDate: bond.maturityDate || bondSnapshot?.maturityDate,
                    });
                }
            } else if (bondSnapshot) {
                // Use bondSnapshot if bondId is not populated
                const id = request.bondId;
                if (id && !bondMap.has(id)) {
                    bondMap.set(id, {
                        _id: id,
                        bondName: bondSnapshot.bondName || 'Unknown Bond',
                        bondNumber: bondSnapshot.bondNumber,
                        issuer: bondSnapshot.issuer,
                        status: bondSnapshot.status,
                        issueDate: bondSnapshot.subscriptionStartDate,
                        maturityDate: bondSnapshot.maturityDate,
                    });
                }
            }
        });

        return Array.from(bondMap.values());
    }, [allocationRequests]);

    // Filter and paginate bonds
    const filteredBonds = React.useMemo(() => {
        if (!approvedBonds) return [];

        const searchLower = searchTerm.toLowerCase();
        return approvedBonds.filter(bond =>
            bond.bondName?.toLowerCase().includes(searchLower) ||
            bond.bondNumber?.toLowerCase().includes(searchLower) ||
            (typeof bond.issuer === 'string' ? bond.issuer.toLowerCase().includes(searchLower) : false)
        );
    }, [approvedBonds, searchTerm]);

    const paginatedBonds = React.useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        return filteredBonds.slice(startIndex, endIndex);
    }, [filteredBonds, currentPage]);

    const totalPages = Math.ceil(filteredBonds.length / PAGE_SIZE);

    // Reset to page 1 when search changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const DocumentCard = ({ section }) => {
        const Icon = section.icon;

        return (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-all">
                <div className="flex items-start justify-between mb-3">
                    <Icon className="w-6 h-6 text-gray-600" />
                    <div className="flex gap-2">
                        <button className="p-1 rounded hover:bg-white/50" title="View">
                            <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="p-1 rounded hover:bg-white/50" title="Download">
                            <Download className="w-4 h-4 text-gray-600" />
                        </button>
                    </div>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{section.title}</h3>
                <p className="text-xs text-gray-700">{section.description}</p>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Document Management
                            </h1>
                            <p className="text-sm text-gray-500">
                                Manage your bond documents 
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Bond Documents</h2>
                </div>

                <div className="mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search bonds..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {isLoadingRequests ? (
                    <LoadingSpinner size="lg" text="Loading bonds..." />
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        {filteredBonds.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                <p>{searchTerm ? 'No bonds match your search' : 'No bonds found'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Bond Name
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Issue Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Maturity Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {paginatedBonds.map((bond) => (
                                                <tr
                                                    key={bond._id}
                                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                                    onClick={() => navigate(`/investor/bonds/${bond._id}/documents`)}
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">{bond.bondName}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {bond.issueDate ? formatDate(bond.issueDate) : 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {bond.maturityDate ? formatDate(bond.maturityDate) : 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {bond.status && getStatusBadge(bond.status)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/investor/bonds/${bond._id}/documents`);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-900"
                                                        >
                                                            View Documents
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                        <div className="flex-1 flex justify-between sm:hidden">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Previous
                                            </button>
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Next
                                            </button>
                                        </div>
                                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                            <div>
                                                <p className="text-sm text-gray-700">
                                                    Showing <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span> to{' '}
                                                    <span className="font-medium">
                                                        {Math.min(currentPage * PAGE_SIZE, filteredBonds.length)}
                                                    </span>{' '}
                                                    of <span className="font-medium">{filteredBonds.length}</span> results
                                                </p>
                                            </div>
                                            <div>
                                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                    <button
                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Previous
                                                    </button>
                                                    {[...Array(totalPages)].map((_, idx) => (
                                                        <button
                                                            key={idx + 1}
                                                            onClick={() => setCurrentPage(idx + 1)}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === idx + 1
                                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {idx + 1}
                                                        </button>
                                                    ))}
                                                    <button
                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages}
                                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Next
                                                    </button>
                                                </nav>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvestorDocumentManagement;
